import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { DataProvider } from './contexts/DataContext.jsx';
import { PersonalizedFeedProvider } from './contexts/PersonalizedFeedContext.jsx';
import Layout from './components/Layout.jsx';
import { ApplicationStateProvider } from './contexts/ApplicationStateContext.jsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Lazy load pages
const HomePage = React.lazy(() => import('./pages/HomePage.jsx'));
const AIFoundryPage = React.lazy(() => import('./pages/AIFoundryPage.jsx')); // AIFoundry page
const AuthPage = React.lazy(() => import('./pages/AuthPage.jsx'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage.jsx'));
const EventsPage = React.lazy(() => import('./pages/EventsPage.jsx'));
const VideoInsightsPage = React.lazy(() => import('./pages/VideoInsightsPage.jsx'));
const DiscoverPage = React.lazy(() => import('./pages/DiscoverPage.jsx'));
const AboutUsPage = React.lazy(() => import('./pages/AboutUsPage.jsx'));
const DisclaimerPage = React.lazy(() => import('./pages/DisclaimerPage.jsx'));
const ContactPage = React.lazy(() => import('./pages/ContactPage.jsx'));

// --- IMPORT  NEW CRICKET ANALYSIS PAGE ---
const AICricketAnalysisPage = React.lazy(() => import('./pages/AICricketAnalysisPage.jsx'));
const AIFootballAnalysisPage = React.lazy(() => import('./pages/AIFootballAnalysisPage.jsx'));
const EnterpriseAIAgentFrameworkPage = React.lazy(() => import('./pages/EnterpriseAIAgentFrameworkPage.jsx'));
const LLMCostDashboardPage = React.lazy(() => import('./pages/LLMCostDashboardPage.jsx'));


const PageLoader = () => (
  <div className="flex justify-center items-center h-[70vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-teal"></div>
  </div>
);

const AppLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-accent-teal mx-auto mb-4"></div>
      <p className="text-slate-600">Loading your experience...</p>
    </div>
  </div>
);

const AppContent = () => {
  const { loading } = useAuth();

  if (loading) {
    return <AppLoader />;
  }

  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* All your existing routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/aifoundry" element={<AIFoundryPage />} />
          <Route path="/auth/callback" element={<AuthPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/videos" element={<VideoInsightsPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* --- ROUTE FOR THE CRICKET & FOOTBALL ANALYSIS PAGE --- */}
          <Route path="/aisportsanalysis/aicricketanalysis" element={<AICricketAnalysisPage />} />
          <Route path="/aisportsanalysis/aifootballanalysis" element={<AIFootballAnalysisPage />} />
          <Route path="/enterprise-ai-agent-framework" element={<EnterpriseAIAgentFrameworkPage />} />
          <Route path="/llm-pricing" element={<LLMCostDashboardPage />} />

        </Routes>
      </Suspense>
    </Layout>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ApplicationStateProvider>
          <DataProvider>
            <PersonalizedFeedProvider>
              <Router>
                <AppContent />
              </Router>
            </PersonalizedFeedProvider>
          </DataProvider>
        </ApplicationStateProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
